/**
 * Menu System Zod Schemas - Types and Enums
 * Production-grade validation schemas for universal platform sync
 */

import { z } from "zod";

// ============================================================================
// DIETARY & ALLERGEN SCHEMAS
// ============================================================================

export const DietaryLabelSchema = z.enum([
  "VEGAN",
  "VEGETARIAN",
  "GLUTEN_FREE",
  "DAIRY_FREE",
  "NUT_FREE",
  "SOY_FREE",
  "EGG_FREE",
  "SHELLFISH_FREE",
  "HALAL",
  "KOSHER",
  "ORGANIC",
  "NON_GMO",
  "KETO_FRIENDLY",
  "PALEO_FRIENDLY",
  "LOW_CARB",
  "LOW_SODIUM",
  "LOW_FAT",
  "HIGH_PROTEIN",
  "WHOLE30",
  "RAW",
  "PESCATARIAN",
]);

export const AllergenSchema = z.enum([
  "MILK",
  "EGGS",
  "FISH",
  "SHELLFISH",
  "TREE_NUTS",
  "PEANUTS",
  "WHEAT",
  "SOYBEANS",
  "SESAME",
  "GLUTEN",
  "MUSTARD",
  "CELERY",
  "LUPIN",
  "MOLLUSCS",
  "SULPHITES",
  "CORN",
  "COCONUT",
]);

export const SpicyLevelSchema = z.enum([
  "NOT_SPICY",
  "MILD",
  "MEDIUM",
  "HOT",
  "EXTRA_HOT",
]);

export const TasteProfileSchema = z.enum([
  "SWEET",
  "SALTY",
  "SOUR",
  "BITTER",
  "UMAMI",
  "SAVORY",
  "SMOKY",
  "TANGY",
]);

// ============================================================================
// CLASSIFICATION SCHEMAS
// ============================================================================

export const ItemTypeSchema = z.enum([
  "FOOD",
  "BEVERAGE",
  "ALCOHOL",
  "ALCOHOLIC_BEVERAGE",
  "NON_ALCOHOLIC_BEVERAGE",
  "MODIFIER",
  "MERCHANDISE",
  "SERVICE",
  "GIFT_CARD",
  "OTHER",
]);

export const ProductCategorySchema = z.enum([
  "PREPARED_FOOD",
  "APPETIZER",
  "ENTREE",
  "SIDE_DISH",
  "DESSERT",
  "SOUP",
  "SALAD",
  "SANDWICH",
  "PIZZA",
  "BURGER",
  "SUSHI",
  "PASTA",
  "SEAFOOD",
  "BAKERY",
  "BEVERAGE",
  "ALCOHOLIC_BEVERAGE",
  "BEER",
  "WINE",
  "SPIRITS",
  "COCKTAIL",
  "NON_ALCOHOLIC_BEVERAGE",
  "SOFT_DRINK",
  "JUICE",
  "COFFEE",
  "TEA",
  "WATER",
  "SMOOTHIE",
  "MILKSHAKE",
  "PACKAGED_FOOD",
  "SNACK",
  "CANDY",
  "DAIRY",
  "PRODUCE",
  "MEAT",
  "FROZEN",
  "MERCHANDISE",
  "GIFT_CARD",
  "SERVICE",
  "OTHER",
]);

export const CuisineTypeSchema = z.enum([
  "AMERICAN",
  "MEXICAN",
  "ITALIAN",
  "CHINESE",
  "JAPANESE",
  "THAI",
  "INDIAN",
  "KOREAN",
  "VIETNAMESE",
  "MEDITERRANEAN",
  "GREEK",
  "MIDDLE_EASTERN",
  "FRENCH",
  "SPANISH",
  "CAJUN",
  "CARIBBEAN",
  "AFRICAN",
  "BBQ",
  "SOUL_FOOD",
  "FAST_FOOD",
  "COMFORT_FOOD",
  "HEALTHY",
  "FUSION",
  "OTHER",
]);

export const PreparationTypeSchema = z.enum([
  "MADE_TO_ORDER",
  "PREPARED_AHEAD",
  "PREPACKAGED",
  "REQUIRES_HEATING",
  "READY_TO_EAT",
  "REQUIRES_ASSEMBLY",
]);

export const PreparationMethodSchema = z.enum([
  "PREPARATION_METHOD_UNSPECIFIED",
  "BAKED",
  "BARBECUED",
  "BASTED",
  "BLANCHED",
  "BOILED",
  "BRAISED",
  "CODDLED",
  "FERMENTED",
  "FRIED",
  "GRILLED",
  "KNEADED",
  "MARINATED",
  "PAN_FRIED",
  "PICKLED",
  "PRESSURE_COOKED",
  "ROASTED",
  "SAUTEED",
  "SEARED",
  "SIMMERED",
  "SMOKED",
  "STEAMED",
  "STEEPED",
  "STIR_FRIED",
  "OTHER_METHOD",
]);

// ============================================================================
// REGULATORY SCHEMAS
// ============================================================================

export const AgeRestrictionSchema = z.enum([
  "NONE",
  "AGE_18",
  "AGE_21",
  "AGE_19",
  "AGE_25",
]);

export const TemperatureCategorySchema = z.enum([
  "HOT",
  "COLD",
  "FROZEN",
  "ROOM_TEMP",
  "AMBIENT",
]);

export const TaxClassificationSchema = z.enum([
  "TEMP_HEATED",
  "TEMP_UNHEATED",
  "TEMP_COLD",
  "CAT_PREPARED_FOOD",
  "CAT_PREPACKAGED_FOOD",
  "CAT_SNACK",
  "CAT_CANDY",
  "CAT_BEVERAGE",
  "CAT_ALCOHOL",
  "CAT_JUICE",
  "CAT_SOFT_DRINK",
  "CAT_WATER",
  "CAT_COFFEE",
  "CAT_TEA",
  "CAT_DAIRY",
  "CAT_BAKERY",
  "CAT_DELI",
  "CAT_PRODUCE",
  "CAT_MEAT",
  "CAT_SEAFOOD",
  "CAT_FROZEN",
  "CAT_ICE_CREAM",
  "CAT_SUPPLEMENT",
  "CAT_OTC_MEDICATION",
  "CAT_TOBACCO",
  "CAT_CANNABIS",
  "CAT_GIFT_CARD",
  "CAT_NON_TAXABLE",
  "TRAIT_PCT_100",
  "TRAIT_PCT_70TO99",
  "TRAIT_PCT_50TO69",
  "TRAIT_CONTAINS_ALCOHOL",
  "TRAIT_CONTAINS_CANNABIS",
  "TRAIT_CARBONATED",
  "TRAIT_NON_CARBONATED",
]);

// ============================================================================
// AVAILABILITY SCHEMAS
// ============================================================================

export const SalesChannelSchema = z.enum([
  "POS",
  "KIOSK",
  "ONLINE_ORDERING",
  "MOBILE_APP",
  "CUSTOMER_INVOICE",
  "UBER_EATS",
  "DOORDASH",
  "GRUBHUB",
  "POSTMATES",
  "CAVIAR",
  "SEAMLESS",
  "YELP",
  "GOOGLE",
  "FACEBOOK",
  "INSTAGRAM",
  "DINE_IN",
  "TAKEOUT",
  "DELIVERY",
  "CURBSIDE",
  "DRIVE_THRU",
  "CATERING",
]);

export const DayOfWeekSchema = z.enum([
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
]);

export const StockStatusSchema = z.enum([
  "IN_STOCK",
  "LOW_STOCK",
  "OUT_OF_STOCK",
  "DISCONTINUED",
  "SEASONAL",
  "PREORDER",
]);

export const SuspensionReasonSchema = z.enum([
  "OUT_OF_STOCK",
  "INGREDIENT_UNAVAILABLE",
  "EQUIPMENT_DOWN",
  "STAFF_SHORTAGE",
  "QUALITY_ISSUE",
  "SEASONAL",
  "TEMPORARY",
  "DISCONTINUED",
  "OTHER",
]);

// ============================================================================
// MEASUREMENT SCHEMAS
// ============================================================================

export const WeightUnitSchema = z.enum([
  "GRAM",
  "KILOGRAM",
  "MILLIGRAM",
  "OUNCE",
  "POUND",
]);

export const VolumeUnitSchema = z.enum([
  "MILLILITER",
  "LITER",
  "FLUID_OUNCE",
  "CUP",
  "PINT",
  "QUART",
  "GALLON",
]);

export const LengthUnitSchema = z.enum(["CENTIMETER", "METER", "INCH", "FOOT"]);

// ============================================================================
// PRICING SCHEMAS
// ============================================================================

export const PricingStrategySchema = z.enum([
  "base",
  "size",
  "variant",
  "open",
  "weight",
  "time_based",
  "market_price",
  "menu_specific",
]);

// ============================================================================
// SYNC SCHEMAS
// ============================================================================

export const SyncStatusSchema = z.enum([
  "NOT_SYNCED",
  "SYNCED",
  "PENDING",
  "SYNCING",
  "ERROR",
  "DISABLED",
]);

export const IntegrationPlatformSchema = z.enum([
  "UBER_EATS",
  "DOORDASH",
  "GRUBHUB",
  "GOOGLE",
  "YELP",
  "FACEBOOK",
  "INSTAGRAM",
  "SQUARE",
  "TOAST",
  "CLOVER",
  "LIGHTSPEED",
  "REVEL",
]);

// ============================================================================
// MODIFIER SCHEMAS
// ============================================================================

export const ModifierDisplayTypeSchema = z.enum([
  "EXPANDED",
  "COLLAPSED",
  "RADIO",
  "CHECKBOX",
  "QUANTITY",
  "NESTED",
]);

export const ModifierPricingStrategySchema = z.enum([
  "DEFAULT",
  "NONE",
  "NO_CHARGE",
  "INDIVIDUAL",
  "GROUP",
  "INCLUDED_THEN_CHARGE",
  "UPCHARGE_ONLY",
]);

// ============================================================================
// MEDIA SCHEMAS
// ============================================================================

export const MediaTypeSchema = z.enum([
  "HERO",
  "THUMBNAIL",
  "GALLERY",
  "ICON",
  "VIDEO",
  "MENU_BOARD",
]);

// ============================================================================
// COMPOSITE SCHEMAS
// ============================================================================

export const NutritionalInfoSchema = z
  .object({
    servingSize: z.number().optional(),
    servingSizeUnit: z.union([WeightUnitSchema, VolumeUnitSchema]).optional(),
    servingsPerContainer: z.number().optional(),
    calories: z.number().optional(),
    caloriesFromFat: z.number().optional(),
    totalFatG: z.number().optional(),
    saturatedFatG: z.number().optional(),
    transFatG: z.number().optional(),
    cholesterolMg: z.number().optional(),
    sodiumMg: z.number().optional(),
    totalCarbsG: z.number().optional(),
    dietaryFiberG: z.number().optional(),
    totalSugarsG: z.number().optional(),
    addedSugarsG: z.number().optional(),
    proteinG: z.number().optional(),
    vitaminAPercent: z.number().optional(),
    vitaminCPercent: z.number().optional(),
    vitaminDPercent: z.number().optional(),
    calciumPercent: z.number().optional(),
    ironPercent: z.number().optional(),
    potassiumPercent: z.number().optional(),
    displayType: z.enum(["SINGLE", "RANGE", "PER_SERVING"]).optional(),
    calorieRange: z
      .object({
        min: z.number(),
        max: z.number(),
      })
      .optional(),
  })
  .strict();

export const PhysicalPropertiesSchema = z
  .object({
    weight: z.number().optional(),
    weightUnit: WeightUnitSchema.optional(),
    length: z.number().optional(),
    width: z.number().optional(),
    height: z.number().optional(),
    dimensionUnit: LengthUnitSchema.optional(),
    volume: z.number().optional(),
    volumeUnit: VolumeUnitSchema.optional(),
    requiresRefrigeration: z.boolean().optional(),
    requiresFreezing: z.boolean().optional(),
    isFragile: z.boolean().optional(),
    isHazmat: z.boolean().optional(),
    shelfLifeDays: z.number().optional(),
    storageInstructions: z.string().optional(),
  })
  .strict();

export const PreparationInfoSchema = z
  .object({
    prepTimeMinutes: z.number().optional(),
    cookTimeMinutes: z.number().optional(),
    totalTimeMinutes: z.number().optional(),
    holdTimeMinutes: z.number().optional(),
    servingTemperature: TemperatureCategorySchema.optional(),
    instructions: z.string().optional(),
    allergenWarning: z.string().optional(),
    cookingMethod: z.string().optional(),
  })
  .strict();

export const AvailabilityWindowSchema = z
  .object({
    dayOfWeek: DayOfWeekSchema,
    startTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, "Must be HH:MM format"),
    endTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, "Must be HH:MM format"),
  })
  .strict();

export const SeasonalAvailabilitySchema = z
  .object({
    isActive: z.boolean(),
    startMonth: z.number().min(1).max(12).optional(),
    startDay: z.number().min(1).max(31).optional(),
    endMonth: z.number().min(1).max(12).optional(),
    endDay: z.number().min(1).max(31).optional(),
    displayMessage: z.string().optional(),
  })
  .strict();

export const StockInfoSchema = z
  .object({
    status: StockStatusSchema,
    quantity: z.number().optional(),
    lowStockThreshold: z.number().optional(),
    suspendedUntil: z.date().optional(),
    suspensionReason: SuspensionReasonSchema.optional(),
    suspensionNote: z.string().optional(),
    lastStockUpdate: z.date().optional(),
  })
  .strict();

export const MediaAssetSchema = z
  .object({
    id: z.string().optional(),
    type: MediaTypeSchema,
    url: z.string().url(),
    thumbnailUrl: z.string().url().optional(),
    altText: z.string().optional(),
    width: z.number().optional(),
    height: z.number().optional(),
    mimeType: z.string().optional(),
    sizeBytes: z.number().optional(),
    position: z.number().optional(),
    isPrimary: z.boolean().optional(),
  })
  .strict();

export const TranslationSchema = z
  .object({
    locale: z.string(),
    name: z.string(),
    description: z.string().optional(),
    posName: z.string().optional(),
    kitchenName: z.string().optional(),
  })
  .strict();

export const PortionSizeSchema = z
  .object({
    quantity: z.number().int().positive(),
    unit: z.array(TranslationSchema).min(1),
  })
  .strict();

export const ExternalPlatformIdSchema = z
  .object({
    platform: IntegrationPlatformSchema,
    externalId: z.string(),
    storeId: z.string().optional(),
    categoryId: z.string().optional(),
    lastSynced: z.date().optional(),
    syncStatus: SyncStatusSchema,
    syncError: z.string().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

export const ChannelPriceOverrideSchema = z
  .object({
    channel: SalesChannelSchema,
    price: z.number().optional(),
    markupPercent: z.number().optional(),
    markupAmount: z.number().optional(),
    isAvailable: z.boolean().optional(),
  })
  .strict();

export const BundledItemSchema = z
  .object({
    itemId: z.number(),
    itemGuid: z.string().optional(),
    quantity: z.number().min(1),
    corePrice: z.number().optional(),
    isCustomizable: z.boolean().optional(),
    modifierGroupIds: z.array(z.number()).optional(),
  })
  .strict();

export const AlcoholInfoSchema = z
  .object({
    containsAlcohol: z.boolean(),
    alcoholByVolume: z.number().optional(),
    alcoholUnitsPerServing: z.number().optional(),
    containerType: z.string().optional(),
    containerSize: z.number().optional(),
    containerSizeUnit: VolumeUnitSchema.optional(),
    countPerOrder: z.number().optional(),
    requiresIdCheck: z.boolean().optional(),
    ageRestriction: AgeRestrictionSchema,
  })
  .strict();

export const CostInfoSchema = z
  .object({
    costOfGoods: z.number().optional(),
    targetMarginPercent: z.number().optional(),
    actualMarginPercent: z.number().optional(),
    laborCostPercent: z.number().optional(),
    supplierCode: z.string().optional(),
    lastCostUpdate: z.date().optional(),
  })
  .strict();

export const AccountingInfoSchema = z
  .object({
    glCode: z.string().optional(),
    revenueCenter: z.string().optional(),
    accountingCategory: z.string().optional(),
    costCenter: z.string().optional(),
    departmentCode: z.string().optional(),
  })
  .strict();

export const DiscoveryInfoSchema = z
  .object({
    keywords: z.array(z.string()).optional(),
    cuisineTypes: z.array(CuisineTypeSchema).optional(),
    tasteProfiles: z.array(TasteProfileSchema).optional(),
    spicyLevel: SpicyLevelSchema.optional(),
    isPopular: z.boolean().optional(),
    isNew: z.boolean().optional(),
    isSignature: z.boolean().optional(),
    isBestSeller: z.boolean().optional(),
    isChefRecommended: z.boolean().optional(),
    isLimitedTime: z.boolean().optional(),
    sortPriority: z.number().optional(),
  })
  .strict();

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type DietaryLabel = z.infer<typeof DietaryLabelSchema>;
export type Allergen = z.infer<typeof AllergenSchema>;
export type SpicyLevel = z.infer<typeof SpicyLevelSchema>;
export type TasteProfile = z.infer<typeof TasteProfileSchema>;
export type ItemType = z.infer<typeof ItemTypeSchema>;
export type ProductCategory = z.infer<typeof ProductCategorySchema>;
export type CuisineType = z.infer<typeof CuisineTypeSchema>;
export type PreparationType = z.infer<typeof PreparationTypeSchema>;
export type PreparationMethod = z.infer<typeof PreparationMethodSchema>;
export type AgeRestriction = z.infer<typeof AgeRestrictionSchema>;
export type TemperatureCategory = z.infer<typeof TemperatureCategorySchema>;
export type TaxClassification = z.infer<typeof TaxClassificationSchema>;
export type SalesChannel = z.infer<typeof SalesChannelSchema>;
export type DayOfWeek = z.infer<typeof DayOfWeekSchema>;
export type StockStatus = z.infer<typeof StockStatusSchema>;
export type SuspensionReason = z.infer<typeof SuspensionReasonSchema>;
export type WeightUnit = z.infer<typeof WeightUnitSchema>;
export type VolumeUnit = z.infer<typeof VolumeUnitSchema>;
export type LengthUnit = z.infer<typeof LengthUnitSchema>;
export type PricingStrategy = z.infer<typeof PricingStrategySchema>;
export type SyncStatus = z.infer<typeof SyncStatusSchema>;
export type IntegrationPlatform = z.infer<typeof IntegrationPlatformSchema>;
export type ModifierDisplayType = z.infer<typeof ModifierDisplayTypeSchema>;
export type ModifierPricingStrategy = z.infer<
  typeof ModifierPricingStrategySchema
>;
export type MediaType = z.infer<typeof MediaTypeSchema>;
export type NutritionalInfo = z.infer<typeof NutritionalInfoSchema>;
export type PhysicalProperties = z.infer<typeof PhysicalPropertiesSchema>;
export type PreparationInfo = z.infer<typeof PreparationInfoSchema>;
export type AvailabilityWindow = z.infer<typeof AvailabilityWindowSchema>;
export type SeasonalAvailability = z.infer<typeof SeasonalAvailabilitySchema>;
export type StockInfo = z.infer<typeof StockInfoSchema>;
export type MediaAsset = z.infer<typeof MediaAssetSchema>;
export type Translation = z.infer<typeof TranslationSchema>;
export type PortionSize = z.infer<typeof PortionSizeSchema>;
export type ExternalPlatformId = z.infer<typeof ExternalPlatformIdSchema>;
export type ChannelPriceOverride = z.infer<typeof ChannelPriceOverrideSchema>;
export type BundledItem = z.infer<typeof BundledItemSchema>;
export type AlcoholInfo = z.infer<typeof AlcoholInfoSchema>;
export type CostInfo = z.infer<typeof CostInfoSchema>;
export type AccountingInfo = z.infer<typeof AccountingInfoSchema>;
export type DiscoveryInfo = z.infer<typeof DiscoveryInfoSchema>;
